import React, { useEffect, useRef, useState } from 'react';
import usePreviewerStore from '@core/stores/usePreviewerStore';
import styles from './FileViewer.module.scss';
import useOverlayStore from '@core/stores/useOverlayStore';
import { isOfficeDocument, getShareableFileUrl } from './fileViewerPreviewUtils';
import { deriveReferenceDownloadFilename } from '@core/shared/referenceFileName';
import { CopyToClipboardButton } from '@core/components/CopyToClipboardButton/CopyToClipboardButton';

const PAGE_HORIZONTAL_PADDING = 24;
const MIN_SCALE = 0.7;
const MAX_SCALE = 2.8;

const MIME_TO_EXTENSION: Record<string, string> = {
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
    'text/plain': 'txt',
    'text/csv': 'csv',
};

export const deriveDownloadFilename = (
    path: string | null | undefined,
    mimeType?: string,
    fallbackExt = 'pdf',
): string => {
    const ext = (mimeType && MIME_TO_EXTENSION[mimeType]) || fallbackExt;
    const fallback = `document.${ext}`;

    if (!path) {
        return fallback;
    }

    try {
        const clean = path.split(/[?#]/)[0];
        const last = clean.substring(clean.lastIndexOf('/') + 1);
        if (!last) {
            return fallback;
        }
        return /\.[a-z0-9]{1,8}$/i.test(last) ? last : `${last}.${ext}`;
    } catch {
        return fallback;
    }
};

const triggerDownload = (url: string, filename: string) => {
    if (!url) {
        return;
    }

    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noopener noreferrer';
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
};

type TPdfDocument = {
    numPages: number;
    getPage: (pageNumber: number) => Promise<{
        getViewport: (options: { scale: number }) => { width: number; height: number };
        render: (options: {
            canvasContext: CanvasRenderingContext2D;
            viewport: { width: number; height: number };
        }) => { promise: Promise<void>; cancel?: () => void };
    }>;
    destroy?: () => Promise<void> | void;
};

export const FileViewer: React.FC = () => {
    const { isPreviewerOpen, file, sourceFilePath, sourceFileName, sourceFileUrl, pageNumber, zIndex, setPreviewerState } =
        usePreviewerStore();
    const { setOverlayState } = useOverlayStore();

    const viewerContentRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    const [pdfDocument, setPdfDocument] = useState<TPdfDocument | null>(null);
    const [localBlobUrl, setLocalBlobUrl] = useState('');
    const [blobMimeType, setBlobMimeType] = useState('');
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [renderError, setRenderError] = useState('');
    const [isPreparingPreview, setIsPreparingPreview] = useState(false);
    const [isRenderingPage, setIsRenderingPage] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [resizeTick, setResizeTick] = useState(0);

    useEffect(() => {
        if (isPreviewerOpen && file) {
            setOverlayState(true, () => setPreviewerState(false));
            setCurrentPage(Math.max(1, pageNumber || 1));
            setRenderError('');
            setIsPreparingPreview(true);
        }

        return () => {
            setOverlayState(false);
        };
    }, [isPreviewerOpen, file, pageNumber, setOverlayState, setPreviewerState]);

    useEffect(() => {
        if (!isPreviewerOpen || !file) {
            setIsPreparingPreview(false);
            return;
        }

        const abortController = new AbortController();
        let isCancelled = false;
        let activeDocument: TPdfDocument | null = null;
        const currentSourcePath = sourceFilePath || file;

        setIsPreparingPreview(true);
        setRenderError('');

        const loadPdfInModal = async () => {
            try {
                const response = await fetch(file, { signal: abortController.signal });
                if (!response.ok) {
                    throw new Error(`Preview fetch failed with status ${response.status}`);
                }

                const blob = await response.blob();
                const data = new Uint8Array(await blob.arrayBuffer());
                if (isCancelled) {
                    return;
                }

                const blobUrl = URL.createObjectURL(blob);
                setLocalBlobUrl((previous) => {
                    if (previous) {
                        URL.revokeObjectURL(previous);
                    }
                    return blobUrl;
                });
                setBlobMimeType(blob.type || '');

                const pdfjs = await import('pdfjs-dist');
                pdfjs.GlobalWorkerOptions.workerSrc = new URL(
                    'pdfjs-dist/build/pdf.worker.min.mjs',
                    import.meta.url,
                ).toString();

                const loadingTask = pdfjs.getDocument({ data });
                const doc = await loadingTask.promise;
                if (isCancelled) {
                    await doc.destroy?.();
                    return;
                }

                activeDocument = doc as unknown as TPdfDocument;
                setPdfDocument((previous) => {
                    previous?.destroy?.();
                    return activeDocument;
                });
                setTotalPages(activeDocument.numPages);
                setCurrentPage((previous) => Math.min(Math.max(previous, 1), activeDocument!.numPages));
            } catch {
                if (!isCancelled) {
                    if (isOfficeDocument(currentSourcePath)) {
                        triggerDownload(
                            file,
                            sourceFileName
                                ? deriveReferenceDownloadFilename(sourceFileName)
                                : deriveDownloadFilename(currentSourcePath),
                        );
                        setPreviewerState(false);
                        return;
                    }

                    setPdfDocument(null);
                    setTotalPages(0);
                    setRenderError('Die Vorschau konnte im Modal nicht geladen werden.');
                }
            } finally {
                if (!isCancelled) {
                    setIsPreparingPreview(false);
                }
            }
        };

        loadPdfInModal();

        return () => {
            isCancelled = true;
            abortController.abort();
            activeDocument?.destroy?.();
        };
    }, [isPreviewerOpen, file, sourceFilePath, sourceFileName, reloadKey, setPreviewerState]);

    useEffect(() => {
        return () => {
            if (localBlobUrl) {
                URL.revokeObjectURL(localBlobUrl);
            }
        };
    }, [localBlobUrl]);

    useEffect(() => {
        if (!isPreviewerOpen || !pdfDocument || !canvasRef.current) {
            return;
        }

        let isCancelled = false;
        let renderTask: { promise: Promise<void>; cancel?: () => void } | null = null;

        const renderPage = async () => {
            try {
                setIsRenderingPage(true);
                setRenderError('');

                const doc = pdfDocument;
                const page = await doc.getPage(currentPage);
                if (isCancelled || !canvasRef.current) {
                    return;
                }

                const baseViewport = page.getViewport({ scale: 1 });
                const targetWidth =
                    (viewerContentRef.current?.clientWidth || baseViewport.width) - PAGE_HORIZONTAL_PADDING;
                const nextScale = Math.min(
                    MAX_SCALE,
                    Math.max(MIN_SCALE, targetWidth / baseViewport.width),
                );
                const viewport = page.getViewport({ scale: nextScale });

                const canvas = canvasRef.current;
                const context = canvas.getContext('2d');
                if (!context) {
                    throw new Error('Canvas context unavailable');
                }

                canvas.width = Math.floor(viewport.width);
                canvas.height = Math.floor(viewport.height);
                canvas.style.width = `${Math.floor(viewport.width)}px`;
                canvas.style.height = `${Math.floor(viewport.height)}px`;

                const currentRenderTask = page.render({ canvasContext: context, viewport });
                renderTask = currentRenderTask;
                await currentRenderTask.promise;
            } catch {
                if (!isCancelled) {
                    setRenderError('Die Seite konnte nicht gerendert werden.');
                }
            } finally {
                if (!isCancelled) {
                    setIsRenderingPage(false);
                }
            }
        };

        renderPage();

        return () => {
            isCancelled = true;
            renderTask?.cancel?.();
        };
    }, [isPreviewerOpen, pdfDocument, currentPage, resizeTick]);

    useEffect(() => {
        if (!isPreviewerOpen) {
            return;
        }

        const onResize = () => {
            setResizeTick((previous) => previous + 1);
        };

        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [isPreviewerOpen]);

    const shareableFileUrl = getShareableFileUrl(sourceFileUrl);

    const onDownload = () => {
        const filename = sourceFileName
            ? deriveReferenceDownloadFilename(sourceFileName)
            : deriveDownloadFilename(sourceFilePath || file, blobMimeType);
        triggerDownload(localBlobUrl || file || '', filename);
    };

    const onRetry = () => {
        setRenderError('');
        setPdfDocument(null);
        setTotalPages(0);
        setIsPreparingPreview(true);
        setReloadKey((previous) => previous + 1);
    };

    const onPrevPage = () => {
        setCurrentPage((previous) => Math.max(previous - 1, 1));
    };

    const onNextPage = () => {
        setCurrentPage((previous) => Math.min(previous + 1, totalPages || previous + 1));
    };

    if (!isPreviewerOpen || !file) {
        return null;
    }

    return (
        <div className={styles.fileViewerContainer} style={{ zIndex }}>
            <div className={styles.toolbar}>
                <div className={styles.toolbarTitle}>Dateivorschau</div>
                <div className={styles.toolbarActions}>
                    <div className={styles.pageControls}>
                        <button
                            type="button"
                            className={styles.actionButton}
                            onClick={onPrevPage}
                            disabled={currentPage <= 1 || isPreparingPreview || isRenderingPage}
                        >
                            Zurueck
                        </button>
                        <span className={styles.pageInfo}>
                            Seite {currentPage}
                            {totalPages > 0 ? ` / ${totalPages}` : ''}
                        </span>
                        <button
                            type="button"
                            className={styles.actionButton}
                            onClick={onNextPage}
                            disabled={
                                totalPages === 0 ||
                                currentPage >= totalPages ||
                                isPreparingPreview ||
                                isRenderingPage
                            }
                        >
                            Weiter
                        </button>
                    </div>
                    <button type="button" className={styles.actionButton} onClick={onDownload}>
                        Download
                    </button>
                    {shareableFileUrl && (
                        <CopyToClipboardButton
                            textToCopy={shareableFileUrl}
                            ButtonComponent={
                                <button type="button" className={styles.actionButton}>
                                    Link kopieren
                                </button>
                            }
                        />
                    )}
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={() => setPreviewerState(false)}
                    >
                        Schliessen
                    </button>
                </div>
            </div>

            {(isPreparingPreview || isRenderingPage) && !renderError && (
                <div className={styles.loadingState}>Vorschau wird geladen...</div>
            )}

            {renderError && (
                <div className={styles.fallbackHint}>
                    <span>{renderError}</span>
                    <div className={styles.fallbackActions}>
                        <button type="button" className={styles.actionButton} onClick={onRetry}>
                            Erneut versuchen
                        </button>
                    </div>
                </div>
            )}

            <div className={styles.viewerContent} ref={viewerContentRef}>
                <div className={styles.viewerCanvasWrapper}>
                    <canvas ref={canvasRef} className={styles.viewerCanvas} />
                </div>
            </div>
        </div>
    );
};
